import { useState } from "react";
import { Plus } from "lucide-react";
import { Outlet, useParams } from "react-router-dom";
import type { Plano } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import { usePlanos } from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { Tabela, type Coluna } from "@/componentes/Tabela";
import { Botao } from "@/componentes/Botao";
import { Pilula } from "@/componentes/PilulaDeSituacao";
import { contagem, dinheiro } from "@/formato";
import { rotaDoPlano } from "@/rotas";
import { NovoPlano } from "./NovoPlano";
import { franquia, rotuloDaPolitica, usaExcedente } from "./plano";

/*
 * Planos — doc 06 §4.
 *
 * Mensalidade, franquia e excedente (emenda ao doc 01 §3.3). Sem busca nem
 * paginação: o catálogo tem meia dúzia de linhas, e é a lista inteira que a
 * diretoria compara.
 */
export function Planos() {
  const { cliente, estado, pode } = useSessao();
  const { id: aberto } = useParams();
  const [criando, setCriando] = useState(false);

  const { data, error, isPending } = usePlanos(cliente, estado.fase === "dentro");
  const planos = data?.planos ?? [];

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-titulo font-semibold">Planos</h1>
        <span className="rotulo">{isPending ? "carregando" : contagem(planos.length, "plano", "planos")}</span>

        {/* Some para quem não é `admin`: a API recusaria com `403`. */}
        {pode("admin") && (
          <Botao className="ml-auto" onClick={() => setCriando(true)}>
            <Plus aria-hidden="true" className="size-4" />
            Novo plano
          </Botao>
        )}
      </header>

      <NovoPlano aberto={criando} aoFechar={() => setCriando(false)} />

      {error ? (
        <p role="alert" className="rounded-cartao border border-falha/40 bg-falha/10 px-4 py-3 text-falha">
          {textoDoErro(error)}
        </p>
      ) : (
        <Tabela
          colunas={COLUNAS}
          itens={planos}
          carregando={isPending}
          chaveDe={(plano) => plano.id}
          selecionado={aberto}
          hrefDe={(plano) => rotaDoPlano(plano.id)}
          rotuloDe={(plano) => plano.nome}
          vazio="Nenhum plano."
        />
      )}

      <Outlet context={planos} />
    </section>
  );
}

/* Em 1024 fica o que a diretoria compara: preço, franquia, excedente e situação. */
const COLUNAS: readonly Coluna<Plano>[] = [
  {
    chave: "nome",
    rotulo: "Plano",
    largura: "minmax(180px, 1.6fr)",
    render: (plano) => (
      <span className={`truncate ${plano.disponivel ? "text-texto" : "text-texto-3"}`}>
        {plano.nome}
      </span>
    ),
  },
  {
    chave: "mensalidade",
    rotulo: "Mensalidade",
    largura: "120px",
    direita: true,
    render: (plano) => <span className="dados">{dinheiro(plano.preco_base_centavos)}</span>,
  },
  {
    chave: "franquia",
    rotulo: "Franquia",
    largura: "120px",
    direita: true,
    render: (plano) => (
      <span className={`dados ${plano.limite_mensal === null ? "text-texto-2" : ""}`}>
        {franquia(plano.limite_mensal)}
      </span>
    ),
  },
  {
    chave: "politica",
    rotulo: "Política",
    largura: "132px",
    descarte: 2,
    render: (plano) => (
      <span className="text-texto-2">{rotuloDaPolitica(plano.politica_excedente)}</span>
    ),
  },
  {
    chave: "excedente",
    rotulo: "Excedente",
    largura: "110px",
    direita: true,
    render: (plano) => (
      <span className="dados text-texto-2">
        {usaExcedente(plano.politica_excedente) ? dinheiro(plano.preco_excedente_centavos) : "—"}
      </span>
    ),
  },
  {
    chave: "contratos",
    rotulo: "Contratos",
    largura: "96px",
    descarte: 1,
    direita: true,
    render: (plano) => <span className="dados">{plano.contratos_vigentes}</span>,
  },
  {
    chave: "disponivel",
    rotulo: "Situação",
    largura: "120px",
    direita: true,
    render: (plano) =>
      plano.disponivel ? (
        <Pilula matiz="pago">disponível</Pilula>
      ) : (
        <Pilula matiz="encerrado">indisponível</Pilula>
      ),
  },
];
